import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { AlertMessagesCmsResource, ArticlesCmsResource, BaseCmsResource, CampaignsCmsResource, CareplanDetailsCmsResource, ContactUsCmsResource, ContentCacheService, DashboardPageCmsResource, GoalsPageCmsResource, GraphDescriptionsCmsResource, JourneyCmsResource, MedicationPageCmsResource, OnboardingsCmsResource, PreferredLanguagesCmsResource, QueryVariables, RegisterPageCmsResource, RelaxationAudiosCmsResource, ResourceClass, SupportPageCmsResource, TermsAndConditionsCmsResource } from '@cardihab/content-cache';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, concatMap, distinctUntilChanged, filter, firstValueFrom, of } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ApprovedStatus, StateService, SyncEventType } from 'src/app/services/state.service';
import { SwManagerService } from 'src/app/services/sw-manager.service';
import { Environment } from 'src/environments/environment';

export type AlertMessage = {
  title: string;
  short_description: string;
  full_description: string;
  variant: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentService {

  currentLocale$ = new ReplaySubject<string>(1);
  alertMessagesMap: { [locator: string]: AlertMessage } = {};
  initializationProgress$: Observable<any>;
  cacheReady$ = new BehaviorSubject<boolean>(false);
  initialized = false;
  refreshTime = Date.now();

  constructor(
    private contentService: ContentCacheService,
    private swManagerService: SwManagerService,
    private stateService: StateService,
    private authService: AuthService,
  ) {
    Preferences.get({ key: 'locale' }).then(result => {
      this.currentLocale$.next(result.value || 'en');
    });
    this.initializationProgress$ = this.contentService.getCacheInitializationProgress();
    this.initializeContentCache();
  }

  async initializeContentCache() {
    App.addListener('resume', async () => {
      const cacheReady = this.cacheReady$.getValue();
      const cacheAge = Date.now() - this.refreshTime;
      const maxAge = 2 * 60 * 60 * 1000; // 2 hours

      if (cacheReady && cacheAge >= maxAge) {
        this.refreshTime = Date.now();
        await this.contentService.refreshCacheAndNetworkResources()
      }
    });
    this.stateService.getPatientStatus().pipe(
      distinctUntilChanged(),
    ).subscribe(async (status) => {
      this.cacheReady$.next(false);
      if (this.initialized) {
        await this.contentService.clearAllCaches()
      }
      try {
        if (status === ApprovedStatus.UNKNOWN) {
          await this.contentService.configureCaching([
            // Cache-first resources (pre-loaded, instant access)
            { resource: ContactUsCmsResource, strategy: 'cache-first', priority: 1 },
            { resource: RegisterPageCmsResource, strategy: 'cache-first', priority: 2 },
            // Network-only resources (always fresh)
            { resource: TermsAndConditionsCmsResource, strategy: 'network-only' },
          ]);
        } else if (status === ApprovedStatus.PRE_APPROVAL) {
          await this.contentService.configureCaching([
            { resource: ArticlesCmsResource, strategy: 'cache-first', priority: 1 },
          ]);
        } else if (status === ApprovedStatus.APPROVED) {
          await this.contentService.configureCaching([
            // Cache-first resources (pre-loaded, instant access)
            { resource: AlertMessagesCmsResource, strategy: 'cache-first', priority: 1 },
            { resource: CampaignsCmsResource, strategy: 'cache-first', priority: 2 },
            { resource: PreferredLanguagesCmsResource, strategy: 'cache-first', priority: 3 },
            { resource: CareplanDetailsCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: ArticlesCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: RelaxationAudiosCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: CampaignsCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: ContactUsCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: OnboardingsCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: DashboardPageCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: JourneyCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: GraphDescriptionsCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: GoalsPageCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: MedicationPageCmsResource, strategy: 'cache-first', priority: 4 },
            { resource: SupportPageCmsResource, strategy: 'cache-first', priority: 4 },
          ]);
        }
        await this.contentService.initializeCache();

        this.cacheReady$.next(true);
        this.refreshTime = Date.now();
        this.initialized = true;
        if (status === ApprovedStatus.APPROVED) {
          this.prefetchAlertMessages();
          this.precacheOnboardingImages();
          firstValueFrom(this.stateService.events$.pipe(filter(result => {
            return SyncEventType.SUCCESS === result || SyncEventType.FAIL === result;
          }))).then(() => {
            this.precacheArticleImages();
          });
        }
      } catch (error) {
        this.cacheReady$.next(false);
        console.error('Failed to initialize cache:', error);
      }
    });
  }

  async precacheOnboardingImages() {
    const onboardingResources: OnboardingsCmsResource[] = await this.contentService.get(OnboardingsCmsResource)
    const results: Set<string> = new Set();
    onboardingResources.forEach((resource: OnboardingsCmsResource) => {
      const res = this.findUrlsInObject(resource);
      res.forEach(res => results.add(res));
    });
    return this.swManagerService.preCacheImages(Array.from(results), 3);
  }

  async precacheArticleImages() {
    const articles: ArticlesCmsResource[] = await this.contentService.get(ArticlesCmsResource)
    const results: Set<string> = new Set();
    articles.forEach((resource: ArticlesCmsResource) => {
      const res = this.findUrlsInObject(resource);
      res.forEach(res => results.add(res));
    });
    return this.swManagerService.preCacheImages(Array.from(results), 3);
  }

  private findUrlsInObject(obj: any): string[] {
    const urlPrefix = Environment.CONTENT_URL + '/media/';
    const results: string[] = [];

    function search(current: any, path = '') {
      if (typeof current === 'string' && current.startsWith(urlPrefix)) {
        results.push(current);
      } else if (typeof current === 'object' && current !== null) {
        for (const [key, value] of Object.entries(current)) {
          const newPath = path ? `${path}.${key}` : key;
          search(value, newPath);
        }
      }
    }

    search(obj);
    return results;
  }


  public async getContent(resource: ResourceClass, variables?: QueryVariables) {
    await firstValueFrom(this.cacheReady$.pipe(filter(Boolean)));
    return await this.contentService.get(resource, variables);
  }

  public watchContent(resource: ResourceClass, variables?: QueryVariables): Observable<BaseCmsResource[]> {
    return this.cacheReady$.pipe(concatMap((cacheReady) => {
      if (cacheReady) {
        return this.contentService.watch(resource, variables);
      }
      return of([]);
    }));
  }

  public getLocale() {
    return this.currentLocale$;
  }

  public async setLocale(locale: string) {
    const result = await Preferences.get({ key: 'locale' });
    const currentLocale = result.value;
    if (currentLocale !== locale) {
      await Preferences.set({ key: 'locale', value: locale });
      this.currentLocale$.next(locale);
    }
    this.contentService.setLocale(locale);
  }

  async prefetchAlertMessages() {
    combineLatest([
      this.watchContent(AlertMessagesCmsResource),
      this.currentLocale$,
    ]).subscribe(([data, locale]) => {
      if (data?.length) {
        let _alertMessagesMap: { [locator: string]: AlertMessage } = {};
        data.forEach((resource) => {
          const entry = resource as AlertMessagesCmsResource;
          if (entry?.locator) {
            _alertMessagesMap[entry.locator] = {
              full_description: entry.fullDescription || '',
              short_description: entry.shortDescription || '',
              title: entry.title || '',
              variant: entry.variant || '',
            };
          }
        });
        Object.assign(this.alertMessagesMap, _alertMessagesMap);
      }
    });
  }

}
